import { useEffect, useRef, useState } from 'react';
import { useFormik } from 'formik';
import { useAppDispatch } from 'store';
import { usePatientInfo } from 'infrastructure/hooks/patient/use-patient-info';
import { useUpdateInteractionStatus } from 'infrastructure/hooks/control-panel';

import { buildWeightBlurb } from '../blurbs';
import { validateStepItems } from '../validate-step';
import { readNurseMaDraft, writeNurseMaDraft } from '../draft';
import { weightCriticalReviewConfig } from './critical-review';
import { weightMonthlyTouchpointConfig } from './monthly-touchpoint';

import type { ICriticalReadingData } from '../types';

interface IProps {
  patientGuid: string;
  idx: string | number;
}

/**
 * Weight Management review logic. Auto-detects monthly-interaction status to pre-fill
 * the "Routine Monthly Interaction this month" field, opens the Monthly Touchpoint when
 * the MA chooses "attempt now", records the interaction on save, and writes the weight note.
 */
export const useWeightLogic = ({ patientGuid, idx }: IProps) => {
  const {
    activityActions: { updateAction },
  } = useAppDispatch();
  const { updateInteractionStatus, updateInteractionStatusLoading } =
    useUpdateInteractionStatus();
  const { patientInfo } = usePatientInfo({ patientGuid });

  const formik = useFormik<ICriticalReadingData>({
    initialValues: readNurseMaDraft(idx),
    onSubmit: () => {},
    validateOnChange: false,
  });
  const { setFieldValue } = formik;

  // Pre-fill the monthly-interaction field from the system-detected status once loaded:
  // interactionStatus === true ⇒ Completed, otherwise ⇒ Not completed (attempt now).
  const interactionStatus = patientInfo?.interactionStatus;
  const appliedInteractionRef = useRef(false);
  useEffect(() => {
    if (interactionStatus === undefined || appliedInteractionRef.current) {
      return;
    }
    appliedInteractionRef.current = true;
    if (formik.values.weightMonthlyInteraction === undefined) {
      setFieldValue(
        'weightMonthlyInteraction',
        interactionStatus ? 'completed' : 'attempt-now',
      );
    }
  }, [interactionStatus, setFieldValue]);

  const isAttemptNow = formik.values.weightMonthlyInteraction === 'attempt-now';
  // Touchpoint is shown only when the MA is attempting the interaction now.
  const [showTouchpoint, setShowTouchpoint] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);

  /** Validate the visible required fields (Critical Review + Touchpoint when shown). */
  const validate = (): string[] => {
    const items = [
      ...weightCriticalReviewConfig,
      ...(isAttemptNow && showTouchpoint ? weightMonthlyTouchpointConfig : []),
    ];
    const found = validateStepItems(items, formik.values);
    setErrors(found);
    return found;
  };

  // Guard against double-submits: the interaction-status flip is an async request and
  // rapid Save clicks would fire it multiple times (and error). `saving` combines the
  // mutation's in-flight state with a re-entry guard for the whole handler.
  const [submitting, setSubmitting] = useState(false);
  const saving = submitting || updateInteractionStatusLoading;

  const handleSave = async (): Promise<boolean> => {
    if (saving) return false;
    if (validate().length > 0) return false;
    setSubmitting(true);
    try {
      // If the MA completed a touchpoint attempt, record the monthly interaction.
      // Flag it on the action so the later comment-send doesn't prompt/flip it again
      // (the activity cache still reads the stale "not interacted" status).
      let interactionStatusUpdated = false;
      if (isAttemptNow && showTouchpoint && !patientInfo?.interactionStatus) {
        await updateInteractionStatus({ patientGuid, isInteracted: true });
        interactionStatusUpdated = true;
      }
      // Touchpoint details belong on the note only when the MA actually attempted
      // it now (clicked "Attempt now"), not merely because the field pre-selected it.
      const blurb = buildWeightBlurb(
        formik.values,
        isAttemptNow && showTouchpoint,
      );
      updateAction({
        idx,
        action: { message: blurb, interactionStatusUpdated },
      });
      writeNurseMaDraft(idx, formik.values);
      return true;
    } catch {
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  return {
    formik,
    isAttemptNow,
    showTouchpoint,
    setShowTouchpoint,
    handleSave,
    errors,
    saving,
  };
};
